import { Drawer } from '@material-ui/core';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import * as Forms from '../../../../components/form-elements/_index';
import { StateSelectList } from '../../../../components/utility/states';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { FlyoutSaveCancel } from '../../../../components/flyout-components/FlyoutSaveCancel';
import { FlyoutTitle } from '../../../../components/flyout-components/FlyoutTitle';
import { serviceCenterSelectList } from '../../../../selectors/userdata-selectors';
import { StatusCode } from '../../../../utils/api';
import { ApiEndpoints } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { Mapper } from '../../../../utils/mapper';
import { ModelValidator } from '../../../../utils/model-validator';
import { DriverStatus } from '../../../../transportation/models/enums/driver-status';
import { DriverCreate } from '../../../../transportation/models/fleet/driver-create';
import { DriverRead } from '../../../../transportation/models/fleet/driver-read';
import { DriverUpdate } from '../../../../transportation/models/fleet/driver-update';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    visible     : boolean;
    onSuccess   : any;
    onClose     : any;
    driver      : DriverRead;
}

export const FleetActionEditDriver = (props: IProps) => {

    const store                                 = useStore();
    const dispatch                              = useDispatch();
    const userServiceCenter                     = useServiceCenter();
    const [service]                             = React.useState(new TransportationSvc());
    const [editDriver, setEditDriver]           = React.useState<DriverRead>(new DriverRead());
    const [errors, setErrors]                   = React.useState({} as any);
    const [saveAttempts, setSaveAttempts]       = React.useState(0);
    const [vehicleEndpoint, setVehicleEndpoint] = React.useState('');
    
    const [serviceCenters] = React.useState(() => {
        return serviceCenterSelectList(store.getState());
    });
    
    React.useEffect(() => {
        if (props.visible && !AppUtils.IsEmpty(props.driver)) {

            const tmpDriver = {...props.driver};

            if (tmpDriver.id) {
                setVehicleEndpoint(ApiEndpoints.TRANSPORTATION_FLEET_VEHICLE_AUTOCOMPLETE.formattify(tmpDriver.serviceCenterId));
            }
            else {
                tmpDriver.status = DriverStatus.Active;
            }
            
            let qry = ApiEndpoints.TRANSPORTATION_FLEET_VEHICLE_AUTOCOMPLETE_NO_SC;
            if (userServiceCenter.name !== 'ALL') {
                tmpDriver.serviceCenterId = userServiceCenter.name;
                qry = ApiEndpoints.TRANSPORTATION_FLEET_VEHICLE_AUTOCOMPLETE.formattify(userServiceCenter.name);
            }

            setEditDriver(tmpDriver);
            setVehicleEndpoint(qry);
        }
    }, [props.visible]);

    /**
     * Save button click handler
     */
    const onSave = async () => {
        const valid = checkValidity();
        if (valid) {
            const response = await service.updateDriver(editDriver);
            if (response.status === StatusCode.Success) {
                props.onSuccess(response.data);
            }
        }
    };

    /**
     * Callback from the AutoComplete selection
     */
    const onAssetChange = (e : any) => {
        const tmpDriver = {...editDriver};

        if (e) {            
            tmpDriver.defaultTruckId = e.id;
            tmpDriver.truckName = e.name;
        }
        else {
            tmpDriver.defaultTruckId = undefined;
        }

        setEditDriver(tmpDriver);
    };

    /**
     * Cleanup some our state and close the panel
     */
    const onClose = () => {
        setEditDriver(undefined as unknown as DriverRead);
        setErrors({});
        setSaveAttempts(0);
        props.onClose();
    };

    /**
     * Cast the current Driver as the appropriate type
     * and run the model validator against it
     * @param d The DriverRead object to check
     */
    const checkValidity = () => {

        const tmpDriver = !editDriver.id 
            ? Mapper.deserialize(DriverCreate, editDriver)
            : Mapper.deserialize(DriverUpdate, editDriver);
        
        const msgs = ModelValidator.validate(tmpDriver);
        setErrors(msgs);
        setSaveAttempts(saveAttempts + 1);

        return AppUtils.IsEmpty(msgs) ? true : false;
    };

    /**
     * Catch-all event handler for updating state
     * @param e The event from the input
     */
    const handleChange = (e : any) => {
        const tmpDriver : any = {...editDriver};

        if (e.target.name === "status") {
            tmpDriver[e.target.name] = e.target.checked ? DriverStatus.Active : DriverStatus.Inactive;
        }
        else {
            tmpDriver[e.target.name] = e.target.value;
        }

        // Should only apply for user's with ALL as their SC
        // but if that changed, update the autocomplete endpoint
        // and clear the tractor info
        if (e.target.name === "serviceCenterId") {
            setVehicleEndpoint(ApiEndpoints.TRANSPORTATION_FLEET_VEHICLE_AUTOCOMPLETE.formattify(tmpDriver.serviceCenterId));
            tmpDriver.defaultTruckId = undefined;
            tmpDriver.truckName = '';
        }

        setEditDriver(tmpDriver);
    };

    /**
     * Update the license expiry date
     * @param e The event from the input
     */
    const handleDriversLicenseExpiryChange = (e : any) => {
        const tmpDriver : DriverRead = {...editDriver};
        tmpDriver.driversLicenseExpiry = e;
        setEditDriver(tmpDriver);
    };
    
    /**
     * Update the physical due date
     * @param e The event from the input
     */
    const handlePhysicalDueDateChange = (e : any) => {
        const tmpDriver : DriverRead = {...editDriver};
        tmpDriver.physicalDueDate = e;
        setEditDriver(tmpDriver);
    };

    /**
     * Check if we've tried to save already, if so
     * trigger the validation check so the user gets
     * up to date feedback
     */
    const handleBlur = () => {
        if (saveAttempts > 0) {
            checkValidity();
        }
    };

    return editDriver
            ? <Drawer 
                anchor="right" 
                onClose={ onClose }
                open={ props.visible }>
                <div className="stop-action-drawer stop-action-basic-info">

                    <FlyoutTitle 
                        onMobileClose={ onClose }
                        title={ editDriver.id > 0 ? "Edit Driver" : "Create Driver"} />

                    <div className="row">
                        <div className="col-md-4">
                            <Forms.SelectInput
                                id="asset-service-center"
                                label="Service Center"
                                name="serviceCenterId"
                                error={ errors.serviceCenterId }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                                options={ serviceCenters }
                                disable={ (editDriver.id === 0 && userServiceCenter.name === "ALL") ? false : true }
                                value={ editDriver.serviceCenterId } />
                        </div>
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-employeeId"
                                type="text"
                                label="Employee ID"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                name="employeeId"
                                error={ errors.employeeId }
                                value={ editDriver.employeeId }
                                onBlur={ handleBlur }                
                                onChange={ handleChange }
                            />
                        </div>
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-samsaraId"
                                type="text"
                                label="Samsara ID"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                name="samsaraId"
                                error={ errors.samsaraId }
                                value={ editDriver.samsaraId }
                                onBlur={ handleBlur }                
                                onChange={ handleChange }
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-fname"
                                type="text"
                                label="First Name"
                                disable={ editDriver.status === DriverStatus.Inactive  }
                                error={ errors.firstName }
                                name="firstName"
                                value={ editDriver.firstName }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-mname"
                                type="text"
                                label="Middle Name"
                                disable={ editDriver.status === DriverStatus.Inactive  }
                                error={ errors.middleName }
                                name="middleName"
                                value={ editDriver.middleName }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-lname"
                                type="text"
                                label="Last Name"
                                name="lastName"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.lastName }
                                value={ editDriver.lastName }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <DataGroupHeader title="Phone Information"/>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <Forms.TelephoneInput
                                id="driver-cell"
                                label="Cell Phone"
                                name="cellPhone"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.cellPhone }
                                value={ editDriver.cellPhone }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>                    
                        <div className="col-md-4">
                            <Forms.TelephoneInput
                                id="driver-cellAlt"
                                label="Cell Phone (Alt)"
                                name="alternatePhone"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.alternatePhone }
                                value={ editDriver.alternatePhone }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <DataGroupHeader title="License Information"/>
                        </div>
                    </div>
                    <div className="row">                        
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-driversLicenseNumber"
                                type="text"
                                label="Driver's License Number"
                                name="driversLicenseNumber"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.driversLicenseNumber }
                                value={ editDriver.driversLicenseNumber }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                        <div className="col-md-4">
                            <Forms.SelectInput
                                id="driver-driversLicenseState"
                                label="Driver's License State"
                                name="driversLicenseState"
                                error={ errors.driversLicenseState }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                                options={ StateSelectList }
                                disable={ editDriver.status === DriverStatus.Inactive }
                                value={ editDriver.driversLicenseState } />                            
                        </div>
                        <div className="col-md-4">
                            <Forms.DateInput
                                id="drivers-licenseExpiry"
                                label="Driver's License Expiry"
                                name="driversLicenseExpiry"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.driversLicenseExpiry }
                                value={ editDriver.driversLicenseExpiry }
                                onChange={ handleDriversLicenseExpiryChange }
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <DataGroupHeader title="Additional Information"/>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <Forms.AutoCompleteInput
                                id="edit-driver-asset"
                                label="Vehicle"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                onChange={ onAssetChange }
                                dispatch={ dispatch }
                                endpoint={ vehicleEndpoint }
                                value={ editDriver.truckName }
                                placeholder="Enter Vehicle Name"
                            />
                        </div>                    
                        <div className="col-md-4">
                            <Forms.DateInput
                                id="drivers-licensphysicalDueDateeExpiry"
                                label="Physical Due Date"
                                name="physicalDueDate"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.physicalDueDate }
                                value={ editDriver.physicalDueDate }
                                onChange={ handlePhysicalDueDateChange }
                            />
                        </div>
                        <div className="col-md-4">
                            <Forms.TextInput
                                id="driver-passportId"
                                type="text"
                                label="Passport ID"
                                name="passportId"
                                disable={ editDriver.status === DriverStatus.Inactive }
                                error={ errors.passportId }
                                value={ editDriver.passportId }
                                onBlur={ handleBlur }
                                onChange={ handleChange }
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Forms.ToggleInput
                                id="driver-status"
                                label="Is Active?"
                                name="status"                                
                                onClick={ handleChange }
                                checked={ editDriver.status === DriverStatus.Active } />
                        </div>
                    </div>
                 
                    <FlyoutSaveCancel
                        onSave={ onSave }
                        onCancel={ onClose }
                    />

                </div>
        </Drawer>
        : null;
};
